import { Link } from '@tanstack/react-router';
import { ArrowUpRight, CheckCircle2, CircleDashed, Circle } from 'lucide-react';
import type { ObjectApartmentSummary, PropertyGroupResponse } from '@/shared/api';
import { useAuth } from '@/shared/auth';
import { guestItems, type GuestItem } from '../object-workspace/objectGuestItems';
import { visibleObjectAreas } from '../object-workspace/objectAreas';
import { useObjectStatusData } from '../object-workspace/useObjectStatusData';

// Was im Portal steht. Nachrichten gehen per E-Mail und OTA, das Gastportal ist diese Seite selbst.
const IN_PORTAL = ['anfahrt', 'parken', 'hausordnung', 'zugang'] as const;
const LOADED = { anfahrt: 'Anfahrt', parken: 'Parken' } as const;

const ICON = { ok: CheckCircle2, open: CircleDashed, neutral: Circle };
const TONE = { ok: 'text-sage', open: 'text-amber', neutral: 'text-ink-faint' };

interface Props {
  group: PropertyGroupResponse;
  apartment?: ObjectApartmentSummary;
  canOpenSources: boolean;
}

/**
 * Jeder Inhalt des Portals mit seinem Stand und dem Sprung in seinen Bereich. Geändert wird dort —
 * hier wird nichts kopiert, und ohne Leserecht entsteht über einen Inhalt keine Aussage.
 */
export function PortalContentList({ group, apartment, canOpenSources }: Props) {
  const areas = visibleObjectAreas(useAuth().user, canOpenSources);
  const status = useObjectStatusData(group, areas);
  const items = guestItems(status, areas, canOpenSources);
  const visible = new Set<string>(areas.map((area) => area.id));
  // Ein Bereich ohne Stand fehlt nicht still: er lädt noch oder konnte nicht geladen werden.
  const rows = IN_PORTAL.flatMap((id): GuestItem[] => {
    const item = items.find((entry) => entry.id === id);
    if (item) return [item];
    if ((id !== 'anfahrt' && id !== 'parken') || !visible.has(id)) return [];
    const failed = status.unavailable?.includes(id);
    return [
      {
        id,
        label: LOADED[id],
        tone: 'neutral',
        detail: failed ? 'Stand konnte nicht geladen werden' : 'Wird geladen …',
        to: `/property-groups/$id/${id}`,
      },
    ];
  });
  const wifi = apartment ? `Je Apartment gepflegt · hier ${apartment.name}` : 'Kein Apartment';

  return (
    <section
      aria-labelledby="portal-contents-title"
      className="border-line/50 bg-card shadow-card rounded-lg border"
    >
      <div className="border-line/60 border-b px-5 py-4">
        <h3 id="portal-contents-title" className="text-ink font-serif text-lg">
          Inhalte im Portal
        </h3>
        <p className="text-ink-soft mt-0.5 text-sm">
          Jeder Inhalt lebt in seinem Bereich. Geändert wird dort – das Portal zeigt immer den
          aktuellen Stand.
        </p>
      </div>
      <ul className="divide-line/60 divide-y px-5">
        {rows.map((item) => (
          <Row
            key={item.id}
            {...item}
            link={canOpenSources ? { to: item.to, id: group.id } : null}
          />
        ))}
        <Row
          label="WLAN"
          detail={wifi}
          tone="neutral"
          link={
            canOpenSources && apartment
              ? { to: '/properties/$id/stammdaten', id: apartment.id }
              : null
          }
        />
      </ul>
    </section>
  );
}

function Row(props: {
  label: string;
  detail: string;
  tone: GuestItem['tone'];
  action?: string;
  link: { to: GuestItem['to'] | '/properties/$id/stammdaten'; id: string } | null;
}) {
  const Icon = ICON[props.tone];
  return (
    <li className="flex min-h-14 items-center gap-3 py-2.5">
      <Icon className={`size-4 shrink-0 ${TONE[props.tone]}`} aria-hidden />
      <div className="min-w-0 flex-1">
        <p className="text-ink text-sm font-semibold">{props.label}</p>
        <p className="text-ink-soft text-xs">{props.detail}</p>
      </div>
      {props.link && (
        <Link
          to={props.link.to}
          params={{ id: props.link.id }}
          aria-label={`${props.label} öffnen`}
          className="text-sage flex min-h-11 shrink-0 items-center gap-1 px-2 text-xs font-semibold hover:underline"
        >
          {props.action ?? 'Öffnen'} <ArrowUpRight className="size-3.5" aria-hidden />
        </Link>
      )}
    </li>
  );
}
